import { mdiPencil } from '@mdi/js'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { useContractTypes, useUpdateContract } from '../../hooks/useQueries'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Label } from '../ui/label'
import { apiErrorMessage, invalidateAssembly, useContractCoverage } from './assembly-api'
import { CoverageSummary } from './CoverageSummary'
import { useQueryClient } from '@tanstack/react-query'

/**
 * The contract page's "Authorization by the assembly" card (spec 2026-10-05-02):
 * the resolution that authorizes the contract (editable) and what it covers.
 * The service refuses anything but a RESOLUTION of the same organization and
 * syndicate; that refusal is shown inline.
 */
export function ContractCoverageCard({
    org,
    contractUid,
    authorizedByUid,
    canWrite,
}: {
    org: string
    contractUid: string
    authorizedByUid?: string | null
    canWrite: boolean
}) {
    const { t } = useTranslation('assemblies')
    const queryClient = useQueryClient()
    const [error, setError] = useState('')
    const [editing, setEditing] = useState(false)
    const { data: coverage } = useContractCoverage(org, contractUid)
    const { data: types } = useContractTypes(org)
    const resolutionTypeUid = (types || []).find((ct) => ct.code === 'RESOLUTION')?.uid

    // GOVERNANCE contracts are left out of the generic list unless asked for.
    const { data: resolutions } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/contracts',
        { params: { path: { orgCode: org }, query: { contract_type_uid: resolutionTypeUid, include_governance: true, limit: 200 } as never } },
        { enabled: !!resolutionTypeUid },
    )
    const options = ((resolutions as { data?: Array<{ uid: string; code?: string; name?: string }> } | undefined)?.data) ?? []

    const update = useUpdateContract(org, contractUid)
    const onChange = (uid: string) => {
        setError('')
        update.mutate({ authorized_by_contract_uid: uid || null } as never, {
            onSuccess: () => {
                setEditing(false)
                invalidateAssembly(queryClient)
            },
            onError: (e: unknown) => setError(t('coverage.save_error', { message: apiErrorMessage(e) })),
        })
    }

    if (!canWrite && !authorizedByUid) return null
    return (
        <Card data-testid="contract-coverage-card">
            <CardHeader>
                <CardTitle className="text-base">{t('coverage.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <CoverageSummary coverage={coverage as never} />
                {/* Display-first: the select only exists once the pencil is clicked. */}
                <div className="space-y-1">
                    <Label htmlFor="authorized-by-select">{t('coverage.authorized_by')}</Label>
                    {!editing && (
                        <div className="flex items-center gap-2 text-sm">
                            <span data-testid="authorized-by-value">
                                {options.find((o) => o.uid === authorizedByUid)?.name
                                    || options.find((o) => o.uid === authorizedByUid)?.code
                                    || t('coverage.authorized_by_none')}
                            </span>
                            {canWrite && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setEditing(true)}
                                    aria-label={t('coverage.edit_authorized_by')}
                                    title={t('coverage.edit_authorized_by')}
                                    data-testid="authorized-by-edit"
                                >
                                    <AppIcon path={mdiPencil} size="sm" />
                                </Button>
                            )}
                        </div>
                    )}
                    {canWrite && editing && (
                        <div className="flex items-center gap-2">
                            <select
                                id="authorized-by-select"
                                data-testid="authorized-by-select"
                                className="block w-full rounded-md border bg-background px-3 py-2 text-sm"
                                value={authorizedByUid || ''}
                                onChange={(e) => onChange(e.target.value)}
                                disabled={update.isPending}
                            >
                                <option value="">{t('coverage.authorized_by_none')}</option>
                                {options.map((o) => (
                                    <option key={o.uid} value={o.uid}>
                                        {o.name || o.code}
                                    </option>
                                ))}
                            </select>
                            <Button variant="ghost" size="sm" onClick={() => { setError(''); setEditing(false) }} data-testid="authorized-by-cancel">
                                {t('cancel')}
                            </Button>
                        </div>
                    )}
                    {error && (
                        <p className="text-sm text-destructive" role="alert" data-testid="authorized-by-error">
                            {error}
                        </p>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}
